import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Upload, X, ArrowLeft, Monitor, Smartphone, GripVertical, Info, Edit } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const API = API_URL;

/**
 * Professional Upload Episode form - Trap Comix dark theme
 * Full-featured with preview, comments, sortable images, and scheduled publishing
 */
const UploadEpisodeForm = ({ seriesId, seriesTitle, episodeId }) => {
  const navigate = useNavigate();
  const isEditMode = Boolean(episodeId);

  const [title, setTitle] = useState("");
  const [episodeNumber, setEpisodeNumber] = useState("");
  const [creatorNotes, setCreatorNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [loadingEpisode, setLoadingEpisode] = useState(false);
  
  // Episode cover state
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [coverUploadedUrl, setCoverUploadedUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  
  // Preview modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewType, setPreviewType] = useState("pc"); // "pc" or "mobile"

  // Delete episode state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingEpisode, setDeletingEpisode] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // ── Episode cover upload handler ──
  const handleCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Cover image must be under 5 MB');
      return;
    }
    setCoverFile(file);
    setCoverPreviewUrl(URL.createObjectURL(file));
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/episode-page`, fd);
      setCoverUploadedUrl(res.data?.url);
    } catch (err) {
      toast.error('Failed to upload cover image');
      setCoverFile(null);
      setCoverPreviewUrl(null);
    } finally {
      setCoverUploading(false);
    }
  };

  const removeCover = async () => {
    if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl);
    setCoverFile(null);
    setCoverPreviewUrl(null);
    setCoverUploadedUrl(null);

    // If editing an existing episode, also remove cover on the server
    if (isEditMode && episodeId) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/chapters/${episodeId}/cover`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        // Non-critical — cover will still be cleared on next save
        console.warn('[EditEpisode] Cover removal API failed:', err);
      }
    }
  };

  // Delete episode handler (soft delete)
  const handleDeleteEpisode = async () => {
    setDeletingEpisode(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/series/${seriesId}/chapters/${episodeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Episode deleted');
      navigate(`/series/${seriesId}`);
    } catch (err) {
      console.error('[DeleteEpisode] Error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete episode');
    } finally {
      setDeletingEpisode(false);
      setShowDeleteModal(false);
    }
  };

  // Load existing episode data in edit mode
  useEffect(() => {
    if (!isEditMode || !episodeId) return;
    setLoadingEpisode(true);
    const token = localStorage.getItem('token');
    axios.get(`${API}/chapters/${episodeId}?edit=true`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => {
        const ep = res.data;
        setTitle(ep.title || '');
        setEpisodeNumber(ep.chapter_number || '');
        setCreatorNotes(ep.author_notes || '');
        setCommentsEnabled(ep.comments_enabled !== false);
        if (ep.cover_url) {
          setCoverUploadedUrl(ep.cover_url);
          setCoverPreviewUrl(ep.cover_url);
        }
        // Load existing pages as file previews
        if (ep.pages_data && ep.pages_data.length > 0) {
          const existingPreviews = ep.pages_data.map(p => p.image_url || p.url || '');
          setFilePreviews(existingPreviews);
          // Create placeholder file objects so the form knows pages exist
          setFiles(ep.pages_data.map((p, i) => ({
            name: `page-${i + 1}`,
            existing: true,
            url: p.image_url || p.url,
          })));
        }
      })
      .catch(err => {
        toast.error('Failed to load episode data');
        console.error('[EditEpisode] Load error:', err);
      })
      .finally(() => setLoadingEpisode(false));
  }, [isEditMode, episodeId]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (newFiles) => {
    // Filter valid image/PDF files
    const validFiles = [];
    const newPreviews = [];

    newFiles.forEach(file => {
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Only JPG, PNG, and PDF files are supported`);
        return;
      }

      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`${file.name}: File size must be less than 2MB`);
        return;
      }

      // Check total files limit (100 max)
      if (files.length + validFiles.length >= 100) {
        toast.error("Maximum 100 images per episode");
        return;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        newPreviews.push({
          name: file.name,
          url: e.target.result
        });
        
        if (newPreviews.length === validFiles.length) {
          setFilePreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFiles = [...files];
    const newPreviews = [...filePreviews];
    
    const draggedFile = newFiles[draggedIndex];
    const draggedPreview = newPreviews[draggedIndex];

    newFiles.splice(draggedIndex, 1);
    newFiles.splice(index, 0, draggedFile);

    newPreviews.splice(draggedIndex, 1);
    newPreviews.splice(index, 0, draggedPreview);

    setFiles(newFiles);
    setFilePreviews(newPreviews);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const validate = () => {
    if (!title.trim()) {
      toast.error("Episode title is required");
      return false;
    }
    if (files.length === 0 && !isEditMode) {
      toast.error("Please upload at least one image");
      return false;
    }
    return true;
  };

  const uploadFiles = async () => {
    const uploadedUrls = [];
    
    for (let i = 0; i < files.length; i++) {
      // Skip already-uploaded existing pages
      if (files[i].existing) {
        uploadedUrls.push({
          image_url: files[i].url,
          url: files[i].url,
          page_number: i + 1
        });
        continue;
      }

      const formData = new FormData();
      formData.append("file", files[i]);

      try {
        const response = await axios.post(`${API}/upload/episode-page`, formData);
        uploadedUrls.push({
          url: response.data?.url,
          page_number: i + 1
        });
      } catch (error) {
        throw new Error(`Failed to upload ${files[i].name}`);
      }
    }

    return uploadedUrls;
  };

  const submit = async (isDraft) => {
    if (!validate() && !isDraft) return;

    setSubmitting(true);
    try {
      // Upload new files (skip existing ones)
      const pagesData = files.length > 0 ? await uploadFiles() : undefined;

      // Build episode payload
      const payload = {
        title: title.trim(),
        chapter_number: episodeNumber.trim() || undefined,
        is_published: isDraft ? "draft" : "published",
        author_notes: creatorNotes.trim() || undefined,
        comments_enabled: commentsEnabled,
        cover_url: coverUploadedUrl || undefined,
      };
      if (pagesData) payload.pages_data = pagesData;

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      let response;
      if (isEditMode) {
        // EDIT: PUT to existing episode — never creates a duplicate
        response = await axios.put(`${API}/chapters/${episodeId}`, payload, { headers });
        toast.success("Episode updated successfully!");
      } else {
        // CREATE: POST new episode
        payload.series_id = seriesId;
        response = await axios.post(`${API}/chapters`, payload, { headers });
        toast.success(isDraft ? "Draft saved successfully" : "Episode published successfully!");
      }

      const respId = response.data?.id || episodeId;
      if (respId && !isDraft) {
        navigate(`/series/${seriesId}/read/${respId}`);
      } else {
        navigate(`/series/${seriesId}`);
      }
    } catch (error) {
      console.error("Error saving episode:", error);
      const detail = error.response?.data?.detail || error.message || "Failed to save episode";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(false);
  };

  const handleSaveDraft = () => {
    submit(true);
  };

  const handlePreview = (type) => {
    setPreviewType(type);
    setShowPreviewModal(true);
  };

  return (
    <div className="min-h-screen bg-black pt-16 pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Loading state for edit mode */}
        {loadingEpisode && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Loading episode...</p>
            </div>
          </div>
        )}

        {!loadingEpisode && (
        <>
        {/* Edit Mode Banner */}
        {isEditMode && (
          <div className="flex items-center gap-3 px-4 py-3 mb-4 bg-amber-500/10 border border-amber-500/40 rounded-xl" data-testid="edit-mode-banner">
            <Edit className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <span className="text-sm font-medium text-amber-300">
              You are editing an existing episode. Changes will update this episode — no duplicate will be created.
            </span>
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={() => navigate(`/series/${seriesId}`)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Series</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
            <h1 className="text-3xl font-bold text-white" data-testid="episode-form-heading">
              {isEditMode ? 'Editing Episode' : 'Upload Episode'}
            </h1>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => handlePreview("pc")}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Monitor className="w-4 h-4" />
                Preview PC
              </button>
              <button
                onClick={() => handlePreview("mobile")}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Smartphone className="w-4 h-4" />
                Preview Mobile
              </button>
              <button
                onClick={handleSaveDraft}
                disabled={submitting}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Draft
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || files.length === 0}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
              >
                {submitting ? (isEditMode ? 'Updating...' : 'Publishing...') : (isEditMode ? 'Update Episode' : 'Publish Episode')}
              </button>
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2 bg-red-900/40 hover:bg-red-800/60 text-red-400 hover:text-red-300 border border-red-800/50 rounded-lg transition-colors flex items-center gap-2"
                  data-testid="editor-delete-episode-btn"
                >
                  <X className="w-4 h-4" />
                  Delete
                </button>
              )}
            </div>
          </div>
          <p className="text-gray-400">
            {seriesTitle ? `Create a new episode for ${seriesTitle}.` : "Create a new episode for this series."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Section 1: Episode Info */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Episode Info</h2>
            
            {/* Episode Title */}
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Episode Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value.substring(0, 120))}
                placeholder="Enter episode title"
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                maxLength={120}
              />
              <p className="text-xs text-gray-500 mt-1">{title.length}/120</p>
            </div>

            {/* Episode Number */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Episode Number <span className="text-gray-500 text-sm font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={episodeNumber}
                onChange={(e) => setEpisodeNumber(e.target.value.substring(0, 10))}
                placeholder="1, 2, Special, etc."
                className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                maxLength={10}
              />
            </div>
          </div>

          {/* Section 2: Episode Cover (Thumbnail) */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6" data-testid="episode-cover-section">
            <h2 className="text-xl font-semibold text-white mb-2">Episode Cover (Thumbnail)</h2>
            <p className="text-sm text-gray-400 mb-4">Shown in the episode list. Recommended 300x400 or wider.</p>
            
            {coverPreviewUrl ? (
              <div className="flex items-start gap-4">
                <div className="relative w-20 h-24 rounded-lg overflow-hidden border border-gray-700 flex-shrink-0" data-testid="episode-cover-preview">
                  <img src={coverPreviewUrl} alt="Episode cover" className="w-full h-full object-cover" />
                  {coverUploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-gray-300">{coverFile?.name || 'Episode cover'}</span>
                  <div className="flex gap-2">
                    <label className="text-xs text-orange-400 hover:text-orange-300 cursor-pointer" data-testid="episode-cover-replace">
                      Replace
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverSelect} className="sr-only" />
                    </label>
                    <button onClick={removeCover} className="text-xs text-red-400 hover:text-red-300" data-testid="episode-cover-remove">
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <label className="flex items-center gap-3 py-3 px-4 bg-gray-800 hover:bg-gray-700 border border-dashed border-gray-600 rounded-lg cursor-pointer transition-colors w-fit" data-testid="episode-cover-upload-btn">
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                <span className="text-sm text-gray-400">Upload Episode Cover</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverSelect} className="sr-only" disabled={coverUploading} />
              </label>
            )}
          </div>

          {/* Section 3: Artwork & Media */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Artwork & Media</h2>
            
            {/* File Upload Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors mb-6 ${
                dragActive 
                  ? "border-orange-500 bg-gray-800" 
                  : "border-gray-700 bg-gray-800/50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-300 text-lg mb-2">Drag and drop image files</p>
              <p className="text-sm text-gray-500 mb-4">or</p>
              <label>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  onChange={handleFileInput}
                  className="sr-only"
                />
                <span className="inline-block px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors font-medium">
                  Select Files
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-4">
                {files.length > 0 ? `${files.length} file(s) uploaded` : "0MB / 200MB"}
              </p>
            </div>

            {/* Image Grid */}
            {files.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-400">{files.length} image(s) - Drag to reorder</p>
                  <button
                    type="button"
                    onClick={() => {
                      setFiles([]);
                      setFilePreviews([]);
                    }}
                    className="text-sm text-red-500 hover:text-red-400 transition-colors"
                  >
                    Remove All
                  </button>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {filePreviews.map((preview, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`relative bg-gray-800 border-2 rounded-lg overflow-hidden cursor-move group ${
                        draggedIndex === index ? "border-orange-500 opacity-50" : "border-gray-700"
                      }`}
                    >
                      {/* Drag Handle */}
                      <div className="absolute top-2 left-2 z-10 bg-gray-900/80 rounded p-1">
                        <GripVertical className="w-4 h-4 text-gray-400" />
                      </div>
                      
                      {/* Delete Button */}
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute top-2 right-2 z-10 bg-red-600 hover:bg-red-700 rounded-full p-1 transition-colors"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>

                      {/* Image Preview */}
                      <div className="aspect-square">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* File Info */}
                      <div className="p-2 bg-gray-900">
                        <p className="text-xs text-gray-400 truncate">Page {index + 1}</p>
                        <p className="text-xs text-gray-500 truncate">{preview.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* File Requirements */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300 space-y-2">
                  <p className="font-semibold text-white">File Requirements:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li><strong>Formats:</strong> JPG, PNG, PDF supported</li>
                    <li><strong>Dimensions:</strong> Recommended width 800-1280px</li>
                    <li><strong>Size:</strong> Max 2MB per image</li>
                    <li><strong>Limit:</strong> Max 100 images per episode</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-2">
                    The system will automatically optimize images that exceed maximum dimensions. 
                    Images may be sliced, resized, or compressed to ensure optimal web performance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Creator's Note */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <label className="block text-white font-semibold mb-2">
              Creator's Note <span className="text-gray-500 text-sm font-normal">(Optional)</span>
            </label>
            <textarea
              value={creatorNotes}
              onChange={(e) => setCreatorNotes(e.target.value.substring(0, 400))}
              rows={4}
              maxLength={400}
              placeholder="Share your thoughts, behind-the-scenes info, or a message for your readers..."
              className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">{creatorNotes.length}/400 characters</p>
          </div>

          {/* Section 4: Comments */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4">Comments</h3>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="comments"
                  checked={commentsEnabled}
                  onChange={() => setCommentsEnabled(true)}
                  className="w-4 h-4 text-orange-500 focus:ring-orange-500 focus:ring-2 bg-gray-800 border-gray-600"
                />
                <span className="text-gray-300">Enable</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="comments"
                  checked={!commentsEnabled}
                  onChange={() => setCommentsEnabled(false)}
                  className="w-4 h-4 text-orange-500 focus:ring-orange-500 focus:ring-2 bg-gray-800 border-gray-600"
                />
                <span className="text-gray-300">Disable</span>
              </label>
            </div>
          </div>

          {/* Bottom Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={submitting}
              className="px-8 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Save Draft
            </button>
            <button
              type="submit"
              disabled={submitting || (!isEditMode && files.length === 0)}
              className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg font-semibold transition-all disabled:cursor-not-allowed"
              data-testid="episode-submit-btn"
            >
              {submitting ? (isEditMode ? 'Updating...' : 'Publishing...') : (isEditMode ? 'Update Episode' : 'Publish Episode')}
            </button>
          </div>
        </form>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="bg-black border-gray-800 text-white max-w-full h-[90vh] overflow-hidden p-0">
          <div className="h-full flex flex-col">
            {/* Preview Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-gray-900">
              <div>
                <DialogTitle className="text-xl font-bold">
                  Episode Preview - {previewType === "pc" ? "Desktop" : "Mobile"}
                </DialogTitle>
                <DialogDescription className="text-gray-400 text-sm">
                  This is how your episode will appear to readers
                </DialogDescription>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-y-auto bg-black">
              <div className={`mx-auto py-8 ${previewType === "mobile" ? "max-w-[390px] px-4" : "max-w-4xl px-8"}`}>
                
                {/* Episode Header */}
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">
                    {title || "Untitled Episode"}
                  </h1>
                  {episodeNumber && (
                    <p className="text-gray-400">Episode {episodeNumber}</p>
                  )}
                  {seriesTitle && (
                    <p className="text-gray-500 text-sm mt-1">Part of {seriesTitle}</p>
                  )}
                </div>

                {/* Episode Images */}
                {filePreviews.length > 0 ? (
                  <div className="space-y-4 mb-8">
                    {filePreviews.map((preview, index) => (
                      <div key={index} className="w-full">
                        <img
                          src={preview.url}
                          alt={`Page ${index + 1}`}
                          className="w-full h-auto rounded-lg"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-900 border-2 border-dashed border-gray-700 rounded-lg p-12 text-center mb-8">
                    <Upload className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No images uploaded yet</p>
                    <p className="text-gray-500 text-sm mt-2">Upload images to see them in the preview</p>
                  </div>
                )}

                {/* Creator's Note */}
                {creatorNotes.trim() && (
                  <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-white mb-3">Creator's Note</h3>
                    <p className="text-gray-300 whitespace-pre-wrap">{creatorNotes}</p>
                  </div>
                )}

                {/* Comments Section */}
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Comments</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      commentsEnabled 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-gray-700 text-gray-400"
                    }`}>
                      {commentsEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  {commentsEnabled ? (
                    <div className="space-y-4">
                      <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
                            <span className="text-gray-400 text-sm">👤</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-400 text-sm">
                              Comments will appear here when readers engage with your episode.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4">
                        <input
                          type="text"
                          placeholder="Add a comment..."
                          disabled
                          className="w-full bg-gray-700 text-gray-400 px-4 py-2 rounded-lg cursor-not-allowed"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">
                      Comments are disabled for this episode.
                    </p>
                  )}
                </div>

                {/* End of Episode Marker */}
                <div className="text-center mt-12 mb-8">
                  <div className="inline-block bg-gray-900 border border-gray-800 rounded-full px-6 py-2">
                    <p className="text-gray-400 text-sm">End of Episode</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 bg-gray-900">
              <p className="text-sm text-gray-400">
                {previewType === "mobile" ? "Mobile" : "Desktop"} view • {filePreviews.length} page(s)
              </p>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Episode Confirmation Modal */}
      {isEditMode && (
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md" data-testid="editor-delete-modal">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                Delete Episode?
              </DialogTitle>
              <DialogDescription className="text-zinc-400 pt-2">
                This will remove the episode from your series and readers will no longer be able to access it.
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingEpisode}
                className="flex-1 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="editor-cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteEpisode}
                disabled={deletingEpisode}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="editor-confirm-delete-btn"
              >
                {deletingEpisode ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      </>
        )}
      </div>
    </div>
  );
};

export default UploadEpisodeForm;
